/**
 * Replacing a file: check, decide, do — in that order, visibly.
 *
 * The press already knows how to purge an upload and publish it again.
 * What it could not do was ASK, and asking is the whole interaction
 * here: a purge is refused when a learner has touched the content, and
 * the only person who can weigh "this evidence is worth keeping"
 * against "this is a draft nobody real ever saw" is the operator
 * looking at the screen.
 *
 * So the refusal is the middle of the flow rather than the end of it.
 * The dialog opens on the PLAN — what would be replaced, and what
 * stands in the way — and the destructive option appears only once
 * there is something to destroy, named and counted, behind a second
 * explicit press.
 *
 * The same component serves one file (from a unit's "from" field) and
 * many (from the publish browser), because they are the same decision at
 * different widths and two dialogs would drift.
 */

import { useCallback, useEffect, useState } from "react";

import type { ReplacePlan, ReplaceResult } from "./types";
import { planReplacement, republish } from "./useLibrary";
import { Badge, Button, Id, Label, MONO, Modal } from "./ui";

type Stage =
  | { at: "checking" }
  | { at: "planned"; plan: ReplacePlan }
  | { at: "running" }
  | { at: "done"; result: ReplaceResult }
  | { at: "failed"; detail: string };

export function RepublishDialog({
  filenames,
  onClose,
  onDone,
}: {
  filenames: string[];
  onClose: () => void;
  /** The registry changed — every count on the page is now stale. */
  onDone: () => void;
}) {
  const [stage, setStage] = useState<Stage>({ at: "checking" });

  const check = useCallback(async () => {
    setStage({ at: "checking" });
    const sent = await planReplacement(filenames);
    setStage(sent.ok ? { at: "planned", plan: sent.data } : { at: "failed", detail: sent.detail });
  }, [filenames]);

  useEffect(() => {
    void check();
  }, [check]);

  const run = async (clearLearnerState: boolean) => {
    setStage({ at: "running" });
    const sent = await republish(filenames, clearLearnerState);
    if (!sent.ok) {
      setStage({ at: "failed", detail: sent.detail });
      return;
    }
    setStage({ at: "done", result: sent.data });
    onDone();
  };

  return (
    <Modal
      testId="republish-dialog"
      title={
        filenames.length === 1
          ? `Purge & republish ${filenames[0]}`
          : `Purge & republish ${filenames.length} files`
      }
      onClose={onClose}
      footer={<Footer stage={stage} onClose={onClose} onRun={run} />}
    >
      <Body stage={stage} filenames={filenames} />
    </Modal>
  );
}

function Footer({
  stage,
  onClose,
  onRun,
}: {
  stage: Stage;
  onClose: () => void;
  onRun: (clear: boolean) => void;
}) {
  if (stage.at === "planned" && stage.plan.blocked) {
    return (
      <>
        <Button onClick={onClose}>Cancel</Button>
        {/* The destructive option is a SEPARATE press from opening the
            dialog, and it names what it destroys.  Nobody should be able
            to delete a learner's history by reflex. */}
        <Button testId="republish-clear" kind="primary" onClick={() => onRun(true)}>
          Delete that learner state and continue
        </Button>
      </>
    );
  }
  if (stage.at === "planned") {
    return (
      <>
        <Button onClick={onClose}>Cancel</Button>
        <Button testId="republish-go" kind="primary" onClick={() => onRun(false)}>
          {stage.plan.replaces.length ? "Purge & republish" : "Publish"}
        </Button>
      </>
    );
  }
  if (stage.at === "done" || stage.at === "failed") {
    return (
      <Button testId="republish-close" kind="primary" onClick={onClose}>
        Close
      </Button>
    );
  }
  return <Button onClick={onClose}>Cancel</Button>;
}

function Body({ stage, filenames }: { stage: Stage; filenames: string[] }) {
  if (stage.at === "checking") {
    return <div style={{ color: "var(--color-text-muted)", fontSize: 13 }}>checking…</div>;
  }
  if (stage.at === "running") {
    return (
      <div style={{ color: "var(--color-text-secondary)", fontSize: 13 }}>
        purging, verifying, republishing…
      </div>
    );
  }
  if (stage.at === "failed") {
    return (
      <div data-testid="republish-failed" style={{ color: "var(--color-error)", fontSize: 13 }}>
        {stage.detail}
      </div>
    );
  }
  if (stage.at === "done") return <Result result={stage.result} />;
  return <Plan plan={stage.plan} filenames={filenames} />;
}

function Plan({ plan, filenames }: { plan: ReplacePlan; filenames: string[] }) {
  const held = [...new Set(plan.blockers.flatMap((blocker) => blocker.learners))];
  return (
    <div style={{ display: "grid", gap: 12, fontSize: 13 }}>
      {plan.unknown.length ? (
        <div style={{ color: "var(--color-error)" }}>
          Not on the shelf: {plan.unknown.join(", ")}
        </div>
      ) : null}

      <div>
        <Label>what happens</Label>
        <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
          {plan.replaces.map((name) => (
            <li key={name}>
              <Id>{name}</Id> — purge what it published, then publish it again from disk
            </li>
          ))}
          {plan.publishes.map((name) => (
            <li key={name}>
              <Id>{name}</Id> — nothing published yet, so this is a plain publish
            </li>
          ))}
          {!plan.replaces.length && !plan.publishes.length ? (
            <li style={{ color: "var(--color-text-muted)" }}>nothing to do</li>
          ) : null}
        </ul>
      </div>

      {plan.blocked ? (
        <div
          data-testid="republish-blockers"
          style={{
            padding: 10,
            borderRadius: 8,
            border: "1px solid var(--color-warning-border)",
            background: "var(--color-warning-bg)",
            color: "var(--color-warning-text)",
          }}
        >
          <b>Learner state is in the way.</b>
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            {plan.blockers.map((blocker) => (
              <li key={blocker.filename} style={{ marginBottom: 4 }}>
                <span style={{ fontFamily: MONO, fontSize: 11.5 }}>{blocker.filename}</span>
                <ul style={{ margin: "2px 0 0", paddingLeft: 16 }}>
                  {blocker.reasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>

          {/* The blast radius is a PERSON, not a file, so the people are
              named before the question rather than counted after it. */}
          {held.length ? (
            <div data-testid="republish-held-by" style={{ marginTop: 8 }}>
              <b>
                Continuing resets {held.length} learner
                {held.length === 1 ? "'s" : "s'"} ENTIRE runtime history
              </b>{" "}
              — every planet, not only this content:
              <div style={{ fontFamily: MONO, fontSize: 11.5, marginTop: 4 }}>
                {held.join(", ")}
              </div>
            </div>
          ) : null}

          <div style={{ marginTop: 8, fontSize: 12 }}>
            A partial delete is what is NOT on offer: dropping only the rows about this file leaves
            the log replaying a history that never happened, and a crossing standing over a state
            that no longer earns it. Whole-learner is the only consistent shape, so it is the one
            this runs — the same reset the admin's learner-runtime page performs. On a live atlas
            the answer is to tombstone the content instead; this exists for a draft nobody real has
            learned from, and it refuses to run in production at all.
          </div>
        </div>
      ) : null}

      {filenames.length > 1 ? (
        <div style={{ color: "var(--color-text-muted)", fontSize: 12 }}>
          All {filenames.length} files are checked together, so you are asked about the evidence
          once rather than once per file.
        </div>
      ) : null}
    </div>
  );
}

function Result({ result }: { result: ReplaceResult }) {
  return (
    <div data-testid="republish-result" style={{ display: "grid", gap: 12, fontSize: 13 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {result.ok ? <Badge tone="good">done</Badge> : <Badge tone="bad">not clean</Badge>}
        <span style={{ color: "var(--color-text-secondary)" }}>
          {result.purgedItems.length} registry row(s) purged and republished
        </span>
      </div>

      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {result.outcomes.map((outcome, index) => (
          <li key={index} style={{ marginBottom: 3 }}>
            <span style={{ fontFamily: MONO, fontSize: 11.5 }}>{outcome.filename || "—"}</span>{" "}
            <b>{outcome.outcome}</b>
            {outcome.detail ? ` — ${outcome.detail}` : ""}
          </li>
        ))}
      </ul>

      {result.cleared && result.cleared.total ? (
        <div data-testid="republish-cleared" style={{ color: "var(--color-warning-text)", fontSize: 12 }}>
          Reset the runtime footprint of {result.cleared.learners.join(", ")} —{" "}
          {result.cleared.total} row(s):{" "}
          {Object.entries(result.cleared.rows)
            .filter(([, count]) => count > 0)
            .map(([label, count]) => `${count} ${label}`)
            .join(", ")}
          .
        </div>
      ) : null}

      {result.leftBehind.length ? (
        // The checkpoint failing is the one outcome worth shouting
        // about: it means the database does not match what the press
        // believes, which is the exact condition this page exists to
        // catch anywhere else.
        <div data-testid="republish-left-behind" style={{ color: "var(--color-error)", fontSize: 12 }}>
          The purge left rows behind: {result.leftBehind.join(", ")}. The registry does not match
          what was asked for — do not treat this file as republished.
        </div>
      ) : null}

      {result.purgedItems.length ? (
        <details>
          <summary style={{ cursor: "pointer", fontSize: 12, color: "var(--color-text-muted)" }}>
            the {result.purgedItems.length} id(s) that were purged
          </summary>
          <div style={{ fontFamily: MONO, fontSize: 11, marginTop: 6, lineHeight: 1.7 }}>
            {result.purgedItems.join(", ")}
          </div>
        </details>
      ) : null}

      <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
        Review notes on the purged rows are kept as history — they detach from the content they
        described rather than disappearing with it, and are no longer exported as current feedback.
      </div>
    </div>
  );
}
